// Stories do produto (estilo Instagram). Comportamento isolado, sem dependencias
// do tema: tira de capas que abre um viewer fullscreen com barras de progresso,
// auto-avanco, navegacao por toque/teclado, segurar para pausar, swipe para
// baixo para fechar (mobile) e som sob demanda. Todo texto pt-BR vem do markup
// (data-attributes / aria-label); este arquivo so cuida do comportamento.

/**
 * @typedef {object} Story
 * @property {string} id - Id estavel do video (media id), usado na persistencia.
 * @property {string} mp4 - URL da fonte mp4.
 * @property {string} poster - URL do poster (preview_image).
 * @property {number} duration - Duracao em ms (fallback de timing).
 */

// Tempo (ms) que o dedo precisa ficar parado para virar "segurar para pausar".
const HOLD_DELAY_MS = 200;
// Movimento minimo (px) para travar a direcao do gesto de 1 dedo.
const DIRECTION_LOCK = 8;
// Arrasto vertical (px) para fechar ao soltar.
const PULL_CLOSE = 120;
// Fracao da largura, a partir da esquerda, que conta como "voltar" no tap.
const TAP_PREV_RATIO = 0.3;

class ProductStories extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLElement | null} */
  #surface = null;
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {HTMLElement[]} */
  #bars = [];
  /** @type {HTMLButtonElement[]} */
  #covers = [];
  /** @type {Story[]} */
  #stories = [];
  /** @type {number} */
  #current = 0;
  // Comeca mutado. A web nao tem acesso ao volume do sistema nem aos botoes
  // fisicos de volume do celular (limitacao do iOS/navegador, sem API), entao
  // nao da para "desmutar quando o usuario aumenta o volume" como o app nativo
  // do Instagram faz. O som liga com um toque no botao dedicado.
  /** @type {boolean} */
  #muted = true;
  /** @type {Set<string>} */
  #seen = new Set();

  // Estado do gesto de ponteiro.
  /** @type {number | null} */
  #pointerId = null;
  /** @type {number} */
  #startX = 0;
  /** @type {number} */
  #startY = 0;
  /** @type {number} */
  #dy = 0;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #holdTimer = null;
  /** @type {boolean} */
  #isHolding = false;
  /** @type {boolean} */
  #isPulling = false;
  /** @type {'undecided' | 'locked' | null} */
  #gesture = null;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-viewer]');
    this.#surface = this.querySelector('[data-surface]');
    this.#video = this.querySelector('[data-video]');
    this.#bars = Array.from(this.querySelectorAll('.story-viewer__bar-fill'));
    this.#covers = Array.from(this.querySelectorAll('.story-cover'));

    if (!this.#dialog || !this.#surface || !this.#video) return;

    this.#stories = this.#covers.map((cover) => ({
      id: cover.dataset.id ?? '',
      mp4: cover.dataset.mp4 ?? '',
      poster: cover.dataset.poster ?? '',
      duration: Number(cover.dataset.duration ?? 0),
    }));

    this.#restoreSeen();

    this.#covers.forEach((cover, index) => {
      cover.addEventListener('click', () => this.open(index));
    });

    this.querySelector('[data-close]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.close();
    });
    // Dois gatilhos de som: o icone no topo (controle persistente) e a
    // mensagem no rodape (some quando o som liga). Ambos alternam o som.
    this.querySelectorAll('[data-sound]').forEach((el) =>
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#toggleSound();
      })
    );
    this.querySelector('[data-prev]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#go(this.#current - 1);
    });
    this.querySelector('[data-next]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#go(this.#current + 1);
    });

    this.#video.addEventListener('timeupdate', this.#handleTimeUpdate);
    this.#video.addEventListener('ended', () => this.#go(this.#current + 1));

    this.#dialog.addEventListener('keydown', this.#handleKeyDown);
    this.#dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    // Clique fora do card (na area do backdrop) fecha. O alvo so e o proprio
    // dialog quando o clique cai fora da superficie centralizada: no desktop,
    // a area ao redor do card; no mobile o card preenche tudo, entao nao dispara.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });

    this.#surface.addEventListener('pointerdown', this.#handlePointerDown);
    this.#surface.addEventListener('pointermove', this.#handlePointerMove);
    this.#surface.addEventListener('pointerup', this.#handlePointerUp);
    this.#surface.addEventListener('pointercancel', this.#handlePointerUp);
  }

  get #storageKey() {
    return `naturativa:stories-seen:${this.dataset.productId ?? ''}`;
  }

  #restoreSeen() {
    try {
      const raw = localStorage.getItem(this.#storageKey);
      if (raw) this.#seen = new Set(JSON.parse(raw));
    } catch {
      this.#seen = new Set();
    }
    this.#covers.forEach((cover) => {
      if (cover.dataset.id && this.#seen.has(cover.dataset.id)) {
        cover.classList.add('is-seen');
      }
    });
  }

  /**
   * Marca um story como visto: persiste e apaga o anel da capa.
   * @param {string} id
   */
  #markSeen(id) {
    if (!id || this.#seen.has(id)) return;
    this.#seen.add(id);
    try {
      localStorage.setItem(this.#storageKey, JSON.stringify(Array.from(this.#seen)));
    } catch {
      // localStorage indisponivel (modo privado): segue sem persistir.
    }
    this.#covers
      .find((cover) => cover.dataset.id === id)
      ?.classList.add('is-seen');
  }

  /**
   * Abre o viewer no story indicado.
   * @param {number} index
   */
  open(index) {
    if (!this.#dialog) return;
    this.#current = index;
    this.#dialog.showModal();
    this.#load(index);
  }

  /**
   * Carrega e toca o story do indice (reseta as barras de progresso).
   * @param {number} index
   */
  #load(index) {
    const story = this.#stories[index];
    if (!story || !this.#video) return;

    this.#video.src = story.mp4;
    this.#video.muted = this.#muted;
    this.#applySoundState();
    this.#video.currentTime = 0;
    this.#video.play().catch(() => {});

    this.#bars.forEach((bar, i) => {
      bar.style.transform = `scaleX(${i < index ? 1 : 0})`;
    });

    this.#markSeen(story.id);
  }

  /**
   * Vai para o story do indice; passou do ultimo, fecha.
   * @param {number} index
   */
  #go(index) {
    if (index >= this.#stories.length) {
      this.close();
      return;
    }
    this.#current = Math.max(0, index);
    this.#load(this.#current);
  }

  close() {
    if (!this.#dialog || !this.#video) return;
    this.#video.pause();
    this.#video.removeAttribute('src');
    this.#video.load();
    this.#resetPull();
    this.#dialog.close();
  }

  #toggleSound() {
    if (!this.#video) return;
    this.#muted = !this.#muted;
    this.#video.muted = this.#muted;
    this.#applySoundState();
  }

  // Reflete o estado de som no icone (classe) e no aria-label (texto pt-BR do
  // markup). Nenhuma string de interface vive no JS.
  #applySoundState() {
    this.classList.toggle('is-sound-on', !this.#muted);
    this.querySelectorAll('[data-sound]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const label = this.#muted ? el.dataset.labelOff : el.dataset.labelOn;
      if (label) el.setAttribute('aria-label', label);
    });
  }

  #handleTimeUpdate = () => {
    const bar = this.#bars[this.#current];
    if (!bar || !this.#video) return;
    const { currentTime, duration } = this.#video;
    if (!Number.isFinite(duration) || duration <= 0) return;
    bar.style.transform = `scaleX(${Math.min(1, currentTime / duration)})`;
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#go(this.#current - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#go(this.#current + 1);
        break;
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-close],[data-sound],[data-prev],[data-next]')) {
      return;
    }
    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#dy = 0;
    this.#isHolding = false;
    this.#isPulling = false;
    this.#gesture = 'undecided';
    this.#surface?.setPointerCapture(event.pointerId);

    this.#holdTimer = setTimeout(() => {
      if (this.#isPulling) return;
      this.#isHolding = true;
      this.#video?.pause();
    }, HOLD_DELAY_MS);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;

    if (this.#gesture === 'undecided' && !this.#isHolding) {
      if (Math.abs(dx) < DIRECTION_LOCK && Math.abs(dy) < DIRECTION_LOCK) return;
      // Vertical para baixo dominante = pull-to-close; qualquer outra coisa nao
      // dispara gesto (tap continua valido ao soltar).
      if (dy > 0 && Math.abs(dy) > Math.abs(dx)) {
        this.#isPulling = true;
        this.#clearHold();
        this.classList.add('is-pulling');
      }
      this.#gesture = 'locked';
    }

    if (this.#isPulling && this.#surface) {
      event.preventDefault();
      this.#dy = Math.max(0, dy);
      this.#surface.style.transition = 'none';
      this.#surface.style.transform = `translateY(${this.#dy}px)`;
      this.#surface.style.opacity = `${Math.max(0.4, 1 - this.#dy / 600)}`;
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (this.#pointerId !== event.pointerId) return;
    this.#clearHold();

    if (this.#isPulling) {
      if (this.#dy > PULL_CLOSE) {
        this.#animateClose();
      } else {
        this.#resetPull();
      }
    } else if (this.#isHolding) {
      this.#video?.play().catch(() => {});
    } else {
      this.#handleTap(event.clientX);
    }

    this.#pointerId = null;
    this.#isHolding = false;
    this.#isPulling = false;
    this.#gesture = null;
  };

  /**
   * Tap simples: lado esquerdo volta, resto avanca.
   * @param {number} clientX
   */
  #handleTap(clientX) {
    if (!this.#surface) return;
    const rect = this.#surface.getBoundingClientRect();
    if (clientX - rect.left < rect.width * TAP_PREV_RATIO) {
      this.#go(this.#current - 1);
    } else {
      this.#go(this.#current + 1);
    }
  }

  #clearHold() {
    if (this.#holdTimer) {
      clearTimeout(this.#holdTimer);
      this.#holdTimer = null;
    }
  }

  #resetPull() {
    this.classList.remove('is-pulling');
    if (!this.#surface) return;
    this.#surface.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
    this.#surface.style.transform = '';
    this.#surface.style.opacity = '';
  }

  #animateClose() {
    if (!this.#surface) {
      this.close();
      return;
    }
    this.#surface.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
    this.#surface.style.transform = 'translateY(100%)';
    this.#surface.style.opacity = '0';
    this.#surface.addEventListener(
      'transitionend',
      () => {
        this.#resetPull();
        this.#surface?.style.removeProperty('transition');
        this.close();
      },
      { once: true }
    );
  }
}

if (!customElements.get('product-stories')) {
  customElements.define('product-stories', ProductStories);
}
